import { timeout } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type StoreService from 'volta/services/store';
import type { ICurrentState } from 'volta/models/types/data-import';
import type IntlService from 'ember-intl/services/intl';

export default class ImportConnectorStatus extends Component<{}> {
  @service store!: StoreService;
  @service intl!: IntlService;

  @tracked queriedImportStatus?: ICurrentState;

  get importIsActive() {
    return this.queriedImportStatus?.steps;
  }

  get importIsPaused() {
    return this.queriedImportStatus?.status === 'Paused';
  }

  get importIsRunning() {
    return this.queriedImportStatus?.status === 'Running';
  }

  get importIsSuccess() {
    return this.queriedImportStatus?.status === 'Success';
  }

  get importIsPausedForPlanningCalculation() {
    return this.queriedImportStatus?.pausedForPlanningCalculation;
  }

  get importIsError() {
    return this.queriedImportStatus?.status === 'Error';
  }

  get labelColor() {
    if (this.importIsActive) {
      if (this.importIsSuccess) return 'success';
      if (this.importIsRunning || this.importIsPaused) return 'info';
      if (this.importIsError) return 'danger';
    }
    return 'info';
  }

  get importPercentComplete() {
    const { steps = [], pendingSteps = [] } = this.queriedImportStatus ?? {};
    return steps && steps.length === 0
      ? undefined
      : (steps.length - pendingSteps.length) / steps.length;
  }

  get importStatus() {
    if (!this.queriedImportStatus) return '';

    const { pausedForPlanningCalculation, pendingSteps, status, currentStep } =
      this.queriedImportStatus ?? {};

    if (pausedForPlanningCalculation) {
      return this.intl.t('importsPage.pausedForPlanning');
    }

    if (!pendingSteps.length) {
      // we don't have any pending steps
      return status;
    }

    return `${status}${!currentStep ? '' : ' - ' + currentStep}`;
  }

  willDestroy(): void {
    this.pollServerForImportStatus.cancelAll();
  }

  @action
  handleDidInsert() {
    this.pollServerForImportStatus.perform();
  }

  @action
  async pauseImport() {
    try {
      this.queriedImportStatus = await this.store.normalPost('jobs/import/pause');
      return this.queriedImportStatus;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  }

  @action
  async resumeImport() {
    try {
      this.queriedImportStatus = await this.store.normalPost('jobs/import/resume');
      return this.queriedImportStatus;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  }

  pollServerForImportStatus = restartableTask(async () => {
    try {
      while (true) {
        this.queriedImportStatus = await this.store.normalGet('jobs/import/state');
        await timeout(5000); // 5 seconds
      }
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });
}
